import { useCallback, useEffect, useMemo, useState } from 'react';

import { isClaimOnRecordedLot, stageLotNdcChange } from '../utils/bulkLotNdcStaging';

/**
 * Is this row still exactly as the last bulk apply left it? A row edited or undone since is the
 * operator's later decision, so "Undo bulk change" leaves it alone and does not count it.
 */
const isStillAsBulkApplied = ({ lastBulkApply, changes, claims, requestLineItemId }) => {
  const appliedChange = lastBulkApply.appliedChanges[requestLineItemId] ?? null;
  const appliedClaim = lastBulkApply.appliedClaims[requestLineItemId] ?? null;
  if (!appliedChange && !appliedClaim) return false;

  return (
    (changes[requestLineItemId] ?? null) === appliedChange &&
    (claims[requestLineItemId] ?? null) === appliedClaim
  );
};

/**
 * POM-6537 — staging what Change NDC & lot applies, and taking it back.
 *
 * One Apply may carry the same lot for other rows of the batch ("Also apply"). The rows it touched,
 * what they held before and what it staged on them are kept, so "Undo bulk change" can put back
 * every one still as it left it. "Bulk lot update" stages the same way, with no edited row. Only
 * the latest bulk apply can be undone; a commit or a discard ends it.
 *
 * `claims` and `setClaim` are useBatchLotClaims': a claim on the recorded lot is staged there,
 * not in `changes` (POM-6536).
 */
const useLotNdcChangeStaging = ({ open, changes, setChanges, claims, setClaim }) => {
  const [lastBulkApply, setLastBulkApply] = useState(null);

  useEffect(() => {
    if (open) setLastBulkApply(null);
  }, [open]);

  // From this render's `changes`: one Apply is one event, and the staged objects are what undo
  // later recognises a row by.
  const withBulkChanges = (staged, bulkChanges) =>
    bulkChanges.reduce((next, bulk) => stageLotNdcChange({ ...bulk, changes: next }), staged);

  const rememberBulkApply = ({ lineItemIds, next, appliedClaims }) =>
    setLastBulkApply({
      lineItemIds,
      previousChanges: Object.fromEntries(lineItemIds.map((id) => [id, changes[id] ?? null])),
      // A row the bulk moves may hold a claim of its own, which Apply clears below.
      previousClaims: Object.fromEntries(lineItemIds.map((id) => [id, claims[id] ?? null])),
      appliedChanges: Object.fromEntries(lineItemIds.map((id) => [id, next[id] ?? null])),
      appliedClaims,
    });

  // A row the bulk moves is a change now, so a claim left on it from before must not return.
  const clearBulkClaims = (bulkChanges) =>
    bulkChanges.forEach((bulk) => setClaim(bulk.requestLineItemId, null));

  const stageApply = ({ item, requestLineItemId, ndc, lotAllocations, bulkChanges = [] }) => {
    const next = withBulkChanges(
      stageLotNdcChange({ changes, item, requestLineItemId, ndc, lotAllocations }),
      bulkChanges
    );
    // POM-6536 — the row keeps its lot and only the permission is new: a claim, not a change.
    const claimLotId = isClaimOnRecordedLot({ item, ndc, lotAllocations })
      ? lotAllocations[0].inventoryLotId
      : null;

    if (bulkChanges.length > 0) {
      rememberBulkApply({
        lineItemIds: [requestLineItemId, ...bulkChanges.map((bulk) => bulk.requestLineItemId)],
        next,
        appliedClaims: { [requestLineItemId]: claimLotId },
      });
    }

    setChanges(next);
    setClaim(requestLineItemId, claimLotId);
    clearBulkClaims(bulkChanges);
  };

  /** "Bulk lot update": one lot staged on several rows, with no row edited first. */
  const stageBulkApply = ({ bulkChanges }) => {
    if (bulkChanges.length === 0) return;

    const next = withBulkChanges(changes, bulkChanges);
    rememberBulkApply({
      lineItemIds: bulkChanges.map((bulk) => bulk.requestLineItemId),
      next,
      appliedClaims: {},
    });
    setChanges(next);
    clearBulkClaims(bulkChanges);
  };

  const undoRow = useCallback(
    (requestLineItemId) => {
      setClaim(requestLineItemId, null);
      setChanges((current) => {
        const next = { ...current };
        delete next[requestLineItemId];

        return next;
      });
    },
    [setChanges, setClaim]
  );

  const stillAppliedIds = useMemo(
    () =>
      (lastBulkApply?.lineItemIds ?? []).filter((requestLineItemId) =>
        isStillAsBulkApplied({ lastBulkApply, changes, claims, requestLineItemId })
      ),
    [lastBulkApply, changes, claims]
  );

  const undoLastBulkApply = useCallback(() => {
    if (!lastBulkApply) return;

    const { previousChanges, previousClaims } = lastBulkApply;
    setChanges((current) => {
      const next = { ...current };
      stillAppliedIds.forEach((id) => {
        if (previousChanges[id]) next[id] = previousChanges[id];
        else delete next[id];
      });

      return next;
    });
    stillAppliedIds.forEach((id) => setClaim(id, previousClaims[id]));
    setLastBulkApply(null);
  }, [lastBulkApply, stillAppliedIds, setChanges, setClaim]);

  const forgetLastBulkApply = useCallback(() => setLastBulkApply(null), []);

  /** What the Pending commit header offers: the rows of the last bulk apply still as it left them. */
  const bulkUndo = useMemo(
    () =>
      stillAppliedIds.length > 0
        ? { count: stillAppliedIds.length, onUndo: undoLastBulkApply }
        : null,
    [stillAppliedIds, undoLastBulkApply]
  );

  return { stageApply, stageBulkApply, undoRow, bulkUndo, forgetLastBulkApply };
};

export default useLotNdcChangeStaging;
